import { IMessagingSettings } from '@dataTypes/messaging-settings.type'
import { IClinicNumber } from '@dataTypes/clinic.type'

export const stripHtmlToPlain = (html: string): string => {
  if (!html) return ''
  if (typeof document !== 'undefined') {
    const container = document.createElement('div')
    container.innerHTML = html
    return (container.textContent ?? container.innerText ?? '')
      .replace(/\s+/g, ' ')
      .trim()
  }
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export const validateAutoReplySettings = (
  settings: Pick<
    IMessagingSettings,
    | 'auto_reply_during_hours_enabled'
    | 'auto_reply_during_hours_message'
    | 'auto_reply_after_hours_enabled'
    | 'auto_reply_after_hours_message'
  >,
): { duringHours?: string; afterHours?: string } => {
  const errors: { duringHours?: string; afterHours?: string } = {}
  if (
    settings.auto_reply_during_hours_enabled &&
    stripHtmlToPlain(settings.auto_reply_during_hours_message).length === 0
  ) {
    errors.duringHours = 'Message is required when auto-reply is enabled.'
  }
  if (
    settings.auto_reply_after_hours_enabled &&
    stripHtmlToPlain(settings.auto_reply_after_hours_message).length === 0
  ) {
    errors.afterHours = 'Message is required when auto-reply is enabled.'
  }
  return errors
}

export const resolveMainClinicNumberId = (
  numbers: IClinicNumber[],
): string | undefined => {
  const main = numbers.find((n) => n.is_main)
  return main?.clinic_number_id ?? numbers[0]?.clinic_number_id
}

export const resolveSmsClinicNumberId = (
  conversation: { clinic_number_id?: string | null } | null | undefined,
  messagingSettings:
    | { default_sms_clinic_number_id?: string | null }
    | null
    | undefined,
  clinicNumbers: IClinicNumber[],
): string | undefined => {
  if (conversation?.clinic_number_id) {
    return conversation.clinic_number_id
  }
  if (messagingSettings?.default_sms_clinic_number_id) {
    return messagingSettings.default_sms_clinic_number_id
  }
  return resolveMainClinicNumberId(clinicNumbers)
}

export const resolveSmsSenderPhone = (
  conversation: { clinic_number_id?: string | null } | null | undefined,
  clinicNumbers: IClinicNumber[],
  messagingSettings:
    | { default_sms_clinic_number_id?: string | null }
    | null
    | undefined,
): string | undefined => {
  const clinicNumberId = resolveSmsClinicNumberId(
    conversation,
    messagingSettings,
    clinicNumbers,
  )
  if (clinicNumberId) {
    const match = clinicNumbers.find(
      (number) =>
        String(number.clinic_number_id || '') === String(clinicNumberId),
    )
    if (match?.phone_number) return match.phone_number
  }
  const fallbackId = resolveMainClinicNumberId(clinicNumbers)
  if (fallbackId) {
    return clinicNumbers.find(
      (number) => String(number.clinic_number_id || '') === String(fallbackId),
    )?.phone_number
  }
  return undefined
}
